import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '~/components/shadcn/button-simple';
import { Card } from '~/components/shadcn/card-simple';
import { Input } from '~/components/shadcn/input-simple';
import { Select } from '~/components/shadcn/select-simple';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/shadcn/table-simple';
import { ConnectedTabs, ConnectedTabsContent } from '~/components/shadcn/tabs-connected';
import api from '~/services/api';
import history from '~/services/history';

import type { FilterCriteria, PaginationState, PurchaseOrder, PurchaseOrderResponse, ReceivingTab, RmaListRow } from './types';

const STATUS_STYLES: Record<string, string> = {
  pending: 'border-amber-200 bg-amber-50 text-amber-700',
  partially_received: 'border-sky-200 bg-sky-50 text-sky-700',
  default: 'border-slate-300 bg-slate-100 text-slate-600',
};

function formatDate(value?: string) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getStatusClass(status?: string) {
  if (!status) return STATUS_STYLES.default;

  const normalizedStatus = status.toLowerCase().trim().replace(/\s+/g, '_');

  return STATUS_STYLES[normalizedStatus] || STATUS_STYLES.default;
}

function formatStatus(status?: string) {
  if (!status) return '-';
  return status.replace(/_/g, ' ');
}

export function NewReceiving() {
  const [activeTab, setActiveTab] = useState<ReceivingTab>('purchase_order');
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [rmas, setRmas] = useState<RmaListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>('po_number');
  const [filterText, setFilterText] = useState('');
  const [appliedFilterText, setAppliedFilterText] = useState('');
  const [rmaFilterText, setRmaFilterText] = useState('');
  const [appliedRmaFilterText, setAppliedRmaFilterText] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 0,
    pageSize: 25,
    totalItems: 0,
  });

  const totalPages = useMemo(() => {
    if (pagination.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(pagination.totalItems / pagination.pageSize));
  }, [pagination.pageSize, pagination.totalItems]);

  const loadPurchaseOrders = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get<PurchaseOrder[] | PurchaseOrderResponse>(`/purchaseOrder?size=${pagination.pageSize}&page=${pagination.currentPage}`, {
        headers: {
          filterReceived: 'pending,partially_received',
          order: 'descend',
          filterCriteria,
          filterText: appliedFilterText,
        },
      });

      const responseData = response?.data;
      let rows: PurchaseOrder[] = [];

      if (Array.isArray(responseData)) {
        rows = responseData;
      } else if (Array.isArray(responseData?.rows)) {
        rows = responseData.rows;
      }

      const responseMeta: PurchaseOrderResponse | null = Array.isArray(responseData) ? null : responseData;
      let nextPageSize: number | null = null;
      if (Number.isInteger(responseMeta?.sizePage)) {
        nextPageSize = Number(responseMeta?.sizePage);
      } else if (Number.isInteger(responseMeta?.pageSize)) {
        nextPageSize = Number(responseMeta?.pageSize);
      }

      setPurchaseOrders(rows);
      setPagination((current) => ({
        currentPage: Number.isInteger(responseMeta?.currentPage) ? Number(responseMeta.currentPage) : current.currentPage,
        pageSize: nextPageSize ?? current.pageSize,
        totalItems: Number.isInteger(responseMeta?.totalItems) ? Number(responseMeta.totalItems) : rows.length,
      }));
    } catch {
      setError('Could not load purchase orders.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilterText, filterCriteria, pagination.currentPage, pagination.pageSize]);

  const loadRmas = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get<RmaListRow[]>('/returns?status=open&include_latest_tracking_update=false');
      const rows = Array.isArray(response?.data) ? response.data : [];

      const normalizedFilter = appliedRmaFilterText.trim().toLowerCase();
      const filteredRows = !normalizedFilter
        ? rows
        : rows.filter((row) => {
            const rmaNumber = String(row.id || '').toLowerCase();
            const tracking = String(row.tracking || '').toLowerCase();
            const carrier = String(row.carrier || '').toLowerCase();
            const customer = String(row.order?.customer?.name || '').toLowerCase();

            return (
              rmaNumber.includes(normalizedFilter) ||
              tracking.includes(normalizedFilter) ||
              carrier.includes(normalizedFilter) ||
              customer.includes(normalizedFilter)
            );
          });

      setRmas(filteredRows);
      setPagination((current) => ({
        ...current,
        totalItems: filteredRows.length,
      }));
    } catch {
      setError('Could not load RMAs.');
    } finally {
      setLoading(false);
    }
  }, [appliedRmaFilterText]);

  useEffect(() => {
    if (activeTab === 'purchase_order') {
      loadPurchaseOrders();
      return;
    }

    if (activeTab === 'rma') {
      loadRmas();
    }
  }, [activeTab, loadPurchaseOrders, loadRmas]);

  useEffect(() => {
    const suffix = ' - New Receiving';
    const baseTitle = document.title.endsWith(suffix) ? document.title.slice(0, -suffix.length) : document.title;

    document.title = `${baseTitle}${suffix}`;

    return () => {
      document.title = baseTitle;
    };
  }, []);

  const handleApplyFilters = () => {
    if (activeTab === 'rma') {
      setAppliedRmaFilterText(rmaFilterText.trim());
      return;
    }

    setPagination((current) => ({
      ...current,
      currentPage: 0,
    }));
    setAppliedFilterText(filterText.trim());
  };

  const handleClearFilters = () => {
    if (activeTab === 'rma') {
      setRmaFilterText('');
      setAppliedRmaFilterText('');
      return;
    }

    setFilterCriteria('po_number');
    setFilterText('');
    setAppliedFilterText('');
    setPagination((current) => ({
      ...current,
      currentPage: 0,
    }));
  };

  const handlePreviousPage = () => {
    setPagination((current) => ({
      ...current,
      currentPage: Math.max(0, current.currentPage - 1),
    }));
  };

  const handleNextPage = () => {
    setPagination((current) => ({
      ...current,
      currentPage: current.currentPage + 1,
    }));
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextSize = Number(event.target.value);
    setPagination((current) => ({
      ...current,
      currentPage: 0,
      pageSize: nextSize,
    }));
  };

  const canGoPrevious = pagination.currentPage > 0;
  const canGoNext = (pagination.currentPage + 1) * pagination.pageSize < pagination.totalItems;
  const tabs = [
    { value: 'purchase_order', label: 'Purchase Order' },
    { value: 'rma', label: 'RMA' },
    { value: 'service_repair', label: 'Service/Repair' },
  ];

  const renderDesktopRows = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
            Loading purchase orders...
          </TableCell>
        </TableRow>
      );
    }

    if (purchaseOrders.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
            No purchase orders found.
          </TableCell>
        </TableRow>
      );
    }

    return purchaseOrders.map((po) => (
      <TableRow key={po.id} className="hover:bg-muted/35">
        <TableCell>
          <Link className="text-[#1890ff] hover:underline" to={`/new-receiving/purchase-order/${po.id}`}>
            {po.po_number || po.id}
          </Link>
        </TableCell>
        <TableCell>
          <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${getStatusClass(po.received)}`}>{formatStatus(po.received)}</span>
        </TableCell>
        <TableCell className="text-muted-foreground">{formatDate(po.qbo_created_at)}</TableCell>
        <TableCell className="font-medium">{po.supplier?.name || '-'}</TableCell>
      </TableRow>
    ));
  };

  const renderRmaRows = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
            Loading RMAs...
          </TableCell>
        </TableRow>
      );
    }

    if (rmas.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
            No RMAs found.
          </TableCell>
        </TableRow>
      );
    }

    return rmas.map((rma) => (
      <TableRow key={rma.id} className="hover:bg-muted/35">
        <TableCell>
          <Link className="text-[#1890ff] hover:underline" to={`/new-receiving/rma/${rma.id}`}>
            {rma.id}
          </Link>
        </TableCell>
        <TableCell>{rma.tracking || '-'}</TableCell>
        <TableCell>{rma.carrier || '-'}</TableCell>
        <TableCell>{rma.order?.customer?.name || '-'}</TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="min-h-screen overflow-auto bg-white xl:pl-60">
      <div style={{ padding: '75px 20px 0 20px' }}>
        <h1 className="mb-[12px] text-2xl font-semibold tracking-tight">New Receivings</h1>

        <section className="rounded-md border border-border bg-background p-4 shadow-sm">
          <ConnectedTabs value={activeTab} onValueChange={(value) => setActiveTab(value as ReceivingTab)} items={tabs}>
            <ConnectedTabsContent value="purchase_order">
              <h2 className="mb-[12px] text-base font-semibold">Purchase Orders</h2>

              {error ? <div className="mb-[12px] rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}

              <div className="mb-4 flex w-full flex-nowrap items-center gap-2">
                <Select
                  value={filterCriteria}
                  onChange={(event) => setFilterCriteria(event.target.value as FilterCriteria)}
                  className="w-[170px] min-w-[170px] max-w-[170px] shrink-0"
                >
                  <option value="po_number">P.O. Number</option>
                  <option value="supplier">Supplier</option>
                </Select>

                <Input
                  value={filterText}
                  onChange={(event) => setFilterText(event.target.value)}
                  placeholder="Type to filter"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') handleApplyFilters();
                  }}
                  className="min-w-0 flex-1"
                />

                <Button className="shrink-0" onClick={handleApplyFilters}>
                  Search
                </Button>
                <Button className="shrink-0" variant="outline" onClick={handleClearFilters}>
                  Clear
                </Button>
              </div>

              <div className="block">
                <Table className="mb-4 rounded-xl border border-border/70 bg-background shadow-sm [&_table]:min-w-full">
                  <TableHeader className="bg-muted/35">
                    <TableRow>
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">P.O. Number</TableHead>
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</TableHead>
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Date</TableHead>
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Supplier</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>{renderDesktopRows()}</TableBody>
                </Table>

                <div className="mt-[16px] flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={handlePreviousPage} disabled={!canGoPrevious}>
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">Page {Math.min(pagination.currentPage + 1, totalPages)}</span>
                    <Button variant="outline" onClick={handleNextPage} disabled={!canGoNext}>
                      Next
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page</span>
                    <Select className="w-[92px]" value={String(pagination.pageSize)} onChange={handlePageSizeChange}>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="hidden">
                {loading ? (
                  <Card className="mb-[12px] p-4">
                    <p className="text-sm text-muted-foreground">Loading purchase orders...</p>
                  </Card>
                ) : (
                  purchaseOrders.map((po) => (
                    <Card
                      key={po.id}
                      className="mb-[12px] cursor-pointer border-l-4 border-l-[#1890ff] bg-gradient-to-r from-[#f0f7ff] to-white p-4 transition-all hover:shadow-[0_4px_12px_rgba(24,144,255,0.3)] active:scale-[0.98]"
                      onClick={() => history.push(`/new-receiving/purchase-order/${po.id}`)}
                    >
                      <div className="mb-[8px] flex items-start justify-between last:mb-0">
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#1890ff]">P.O. Number</div>
                          <div className="text-[16px] font-medium text-[#1890ff]">{po.po_number || po.id}</div>
                        </div>

                        <div className="text-right">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#1890ff]">Status</div>
                          <span className={`inline-block rounded-[4px] border px-[8px] py-[2px] text-[12px] font-medium ${getStatusClass(po.received)}`}>
                            {po.received || '-'}
                          </span>
                        </div>
                      </div>

                      <div className="mb-[8px] flex items-start justify-between last:mb-0">
                        <div className="flex-1">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#1890ff]">Supplier</div>
                          <div className="text-[14px] font-medium text-[#262626]">{po.supplier?.name || '-'}</div>
                        </div>

                        <div className="text-right">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#1890ff]">Date</div>
                          <div className="text-[14px] font-medium text-[#8c8c8c]">{formatDate(po.qbo_created_at)}</div>
                        </div>
                      </div>
                    </Card>
                  ))
                )}

                {!loading && purchaseOrders.length > 0 ? (
                  <div className="mt-[16px] text-center">
                    <div className="inline-flex items-center gap-2">
                      <Button variant="outline" disabled={!canGoPrevious} onClick={handlePreviousPage}>
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">Page {Math.min(pagination.currentPage + 1, totalPages)}</span>
                      <Button variant="outline" disabled={!canGoNext} onClick={handleNextPage}>
                        Next
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </ConnectedTabsContent>

            <ConnectedTabsContent value="rma">
              <h2 className="mb-[12px] text-base font-semibold">RMA</h2>

              {error ? <div className="mb-[12px] rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}

              <div className="mb-4 flex w-full flex-nowrap items-center gap-2">
                <Input
                  value={rmaFilterText}
                  onChange={(event) => setRmaFilterText(event.target.value)}
                  placeholder="Search by RMA Number, Tracking Number, Carrier, or Customer"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') handleApplyFilters();
                  }}
                  className="min-w-0 flex-1"
                />

                <Button className="shrink-0" onClick={handleApplyFilters}>
                  Search
                </Button>
                <Button className="shrink-0" variant="outline" onClick={handleClearFilters}>
                  Clear
                </Button>
              </div>

              <div className="block">
                <Table className="mb-4 rounded-xl border border-border/70 bg-background shadow-sm [&_table]:min-w-full">
                  <TableHeader className="bg-muted/35">
                    <TableRow>
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">RMA Number</TableHead>
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tracking Number</TableHead>
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Carrier</TableHead>
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Customer</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>{renderRmaRows()}</TableBody>
                </Table>
              </div>
            </ConnectedTabsContent>

            <ConnectedTabsContent value="service_repair" className="p-6">
              <h2 className="mb-2 text-base font-semibold">Service/Repair</h2>
              <p className="text-sm text-muted-foreground">TBD</p>
            </ConnectedTabsContent>
          </ConnectedTabs>
        </section>
      </div>
    </div>
  );
}
